import { createContext, ReactNode, useContext, useMemo, useState, SetStateAction, Dispatch } from "react";
import { Box } from "@mui/material";
import { useNode, useEditor } from "@craftjs/core";
import uniq from "lodash/uniq";
import slugify from "slugify";
import { Spacing } from "@/components/editor/settings/SpacingSettings/SpacingSettings";
import useNormalizeSpacing from "@/components/editor/settings/SpacingSettings/useNormalizeSpacing";
import SectionSettings from "@/components/editor/settings/SectionSettings";
import EmptyState from "@/components/EmptyState";
import { TransitionFlip } from "@/components/editor/settings/SectionTransitionSetting";
import Shapes, { ShapeKeys } from "./shapes/Shapes";
import useNormalizeBackground from "@/components/editor/settings/BackgroundSettings/useNormalizeBackground";
import { MetaDataProps } from "../../settings/IndexSettings";
import useResponsiveValues, { ResponsiveValue } from "@/components/editor/hooks/useResponsiveValues";
import { CssClass } from "@/components/editor/settings/ClassSettings";
import { MenuContext } from "@/pages/menu/Menu";
import useVariableValues from "../../hooks/useVariableValues";
import { MenuSection } from "@/types";

const Text = ({ children }: { children: string | number }) => {
  const vv = useVariableValues();
  return vv.getValue(children);
};

type TransitionReflectOptions = Partial<Record<TransitionFlip, boolean>>;

type ISectionContext = Partial<MenuSection> & {
  selectedCategories: string[];
  setSelectedCategories: Dispatch<SetStateAction<string[]>>;
  availableDishes: number | null;
  setAvailableDishes: Dispatch<SetStateAction<number | null>>;
};

export const SectionContext = createContext<ISectionContext>({
  category: undefined,
  categoryTitle: undefined,
  selectedCategories: [],
  setSelectedCategories: () => undefined,
  availableDishes: null,
  setAvailableDishes: () => undefined,
});

interface Props extends Spacing, TransitionReflectOptions, MetaDataProps, CssClass {
  children: ReactNode;
  backgroundImage: string;
  // section transition
  transitionTop?: ShapeKeys;
  transitionBottom?: ShapeKeys;
  transitionTopColor?: string;
  transitionBottomColor?: string;
  scale: number | ResponsiveValue<number>;
}

const SectionBlock = (props: Partial<Props>) => {
  const {
    children,
    // section transition
    transitionTop,
    transitionBottom,
    transitionTopColor,
    transitionBottomColor,
    transitionTopFlipVertical,
    transitionBottomFlipVertical,
    transitionTopFlipHorizontal,
    transitionBootomFlipHorizontal,
    // meta data
    title = "",
    subtitle,
    showMetaData,
    scale,
  } = props;
  const responsive = useResponsiveValues();
  const background = useNormalizeBackground(props);
  const spacing = useNormalizeSpacing(props);
  // const { menu, template, sections } = useContext(TheMenuContext);
  const menuContext = useContext(MenuContext);
  const { menu, template, sections } = menuContext;
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [availableDishes, setAvailableDishes] = useState<number | null>(null);

  const styles = useMemo(() => {
    return { ...spacing, ...background };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const {
    id,
    connectors: { connect, drag },
  } = useNode();

  const { isEmpty } = useEditor((_, query) => ({
    isEmpty: !query.node(id).childNodes().length,
  }));

  const sectionId = useMemo(() => slugify(title ?? "", { lower: true }), [title]);

  const topTransform = `scale(${transitionTopFlipHorizontal ? -1 : 1}, ${transitionTopFlipVertical ? -1 : 1})`;

  const bottomTransform = `scale(${transitionBootomFlipHorizontal ? -1 : 1}, ${transitionBottomFlipVertical ? -1 : 1})`;

  const sectionSettings = useMemo(() => {
    const structure = menu?.structure ?? template?.structure ?? [];

    if (!sections?.length || !structure?.length) return {} as unknown as MenuSection;

    const sectionIndex = sections.findIndex((s) => s.id === id);

    return structure[sectionIndex] ?? ({} as unknown as MenuSection);
  }, [menu?.structure, template?.structure, sections, id]);

  const classNames = useMemo(() => {
    let draft = ["section"];
    
    const viewportClassName = responsive.getValue(props?.cssClass);
    
    if (viewportClassName) {
      draft.push(viewportClassName.trim());
    }
    
    if (sectionSettings?.category) {
      draft.push(sectionSettings.category);
    }
    
    return uniq(draft).join(" ");
  }, [props?.cssClass, responsive, selectedCategories, sectionSettings?.category]);

  if ((!sectionSettings?.category && menu?.structure) || sectionSettings?.isVisible === false || availableDishes === 0) {
    return null;
  }

  return (
    <SectionContext.Provider
      value={{
        ...sectionSettings,
        selectedCategories,
        setSelectedCategories,
        availableDishes,
        setAvailableDishes,
      }}
    >
      <div
        style={styles}
        className={classNames}
        id={`section-${sectionId}`}
        key={`${id}-${responsive.viewport}`}
        ref={(ref: any) => connect(drag(ref))}
      >
        {transitionTop ? (
          <Box
            className="section-transition--top"
            sx={{
              "& svg": {
                verticalAlign: "top",
                WebkitTransform: topTransform,
                transform: topTransform,
                marginTop: "-1px",
              },
            }}
          >
            <Shapes shape={transitionTop} color={transitionTopColor} scale={responsive.getValue(scale)} />
          </Box>
        ) : null}
        {showMetaData && title ? (
          <h2><Text>{title}</Text></h2>
        ) : null}
        {showMetaData && subtitle ? (
          <h3>
            <Text>{subtitle}</Text>
          </h3>
        ) : null}
        {isEmpty ? <EmptyState /> : children}
        {transitionBottom ? (
          <Box
            className="section-transition--bottom"
            sx={{
              "& svg": {
                verticalAlign: "bottom",
                WebkitTransform: bottomTransform,
                transform: bottomTransform,
                marginBottom: "-1px",
              },
            }}
          >
            <Shapes shape={transitionBottom} color={transitionBottomColor} scale={responsive.getValue(scale)} />
          </Box>
        ) : null}
      </div>
    </SectionContext.Provider>
  );
};

SectionBlock.craft = {
  displayName: "Sección",
  props: {},
  rules: {
    canDrag: () => true,
    canDrop: () => true,
  },
  related: {
    settings: SectionSettings,
  },
};

export default SectionBlock;
